import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Settings as SettingsIcon,
  Palette,
  LogOut,
  Download,
  PartyPopper,
  Menu as MenuIcon,
  X as XIcon,
  Pin,
  PinOff,
  Bell,
  Users,
  BookOpen,
  Search,
  type LucideIcon,
} from "lucide-react";
import { ThemePickerDropdown } from "./ThemePicker";
import { CrossAppNav } from "./CrossAppNav";
import { useAuth } from "../lib/auth";
import {
  hasNativePrompt,
  triggerNativePrompt,
  isStandalone,
  isIosSafari,
  subscribe as subscribeInstall,
} from "../lib/install-prompt";
import { useChangelog } from "../lib/changelog";

interface NavEntry {
  to: string;
  label: string;
  Icon: LucideIcon;
  matches: (pathname: string) => boolean;
  /** Tailwind classes applied to the entry's icon-tile when active.
   *  Mirrors the per-tab accent the cockpit had on the top nav so the
   *  user keeps the colour mapping (Today=sky, Capture=emerald, etc.). */
  activeTile: string;
  /** Inactive icon colour tint for the icon-only collapsed state. */
  inactiveIconColor: string;
}

const under = (base: string) => (p: string) => p === base || p.startsWith(`${base}/`);

const SETTINGS: NavEntry = {
  to: "/settings",
  label: "Settings",
  Icon: SettingsIcon,
  matches: under("/settings"),
  activeTile: "bg-slate-300 text-slate-900 shadow-md ring-2 ring-slate-200/60",
  inactiveIconColor: "text-slate-300",
};

/** Step 32: 6-mode information architecture. The long flat list is
 *  replaced by six top-level modes; each is a hub that owns a set of
 *  sub-routes (still individually deep-linkable). `matches` lights
 *  the mode whenever the path is inside any route it owns, including
 *  the /u/:handle prefixed variants. Settings stays out of the six
 *  and is reached via the pinned gear below. */
const ownsAny =
  (prefixes: string[]) =>
  (p: string): boolean => {
    const path = p.replace(/^\/u\/[^/]+/, "") || "/";
    return prefixes.some((b) =>
      b === "/" ? path === "/" : path === b || path.startsWith(`${b}/`),
    );
  };

/** Day 7 (W076.4): six top-level surfaces replace the legacy six
 *  modes. Each is qualitatively different (curated, queue, dossier,
 *  long-term knowledge, ask-anything, settings) rather than yet
 *  another bullet list. */
const SURFACE_TODAY: NavEntry = {
  to: "/today",
  label: "Today",
  Icon: Sparkles,
  matches: (p) =>
    ownsAny(["/", "/today", "/now"])(p) || /^\/u\/[^/]+\/?$/.test(p),
  activeTile: "bg-sky-500 text-white shadow-md ring-2 ring-sky-300/40",
  inactiveIconColor: "text-sky-400",
};
const SURFACE_ATTENTION: NavEntry = {
  to: "/attention",
  label: "Attention",
  Icon: Bell,
  matches: ownsAny([
    "/attention",
    "/engagements",
    "/actions",
    "/intents",
    "/recommendations",
    "/commitments",
    "/forgotten",
    "/needs-review",
    "/observations",
    "/goals",
    "/conversations",
  ]),
  activeTile: "bg-rose-500 text-white shadow-md ring-2 ring-rose-300/40",
  inactiveIconColor: "text-rose-400",
};
const SURFACE_PEOPLE: NavEntry = {
  to: "/people",
  label: "People",
  Icon: Users,
  matches: ownsAny(["/people", "/orgs", "/me"]),
  activeTile: "bg-indigo-500 text-white shadow-md ring-2 ring-indigo-300/40",
  inactiveIconColor: "text-indigo-400",
};
const SURFACE_LIBRARY: NavEntry = {
  to: "/library",
  label: "Library",
  Icon: BookOpen,
  matches: ownsAny([
    "/library",
    "/agenda",
    "/timeline",
    "/places",
    "/transactions",
    "/things",
    "/wishes",
    "/decisions",
    "/routines",
    "/reflect",
    "/health",
    "/financial",
    "/flow",
  ]),
  activeTile: "bg-emerald-500 text-white shadow-md ring-2 ring-emerald-300/40",
  inactiveIconColor: "text-emerald-400",
};
const SURFACE_RECALL: NavEntry = {
  to: "/recall",
  label: "Recall",
  Icon: Search,
  matches: ownsAny(["/recall", "/explore"]),
  activeTile: "bg-violet-500 text-white shadow-md ring-2 ring-violet-300/40",
  inactiveIconColor: "text-violet-400",
};

const PRIMARY_NAV: NavEntry[] = [
  SURFACE_TODAY,
  SURFACE_ATTENTION,
  SURFACE_PEOPLE,
  SURFACE_LIBRARY,
  SURFACE_RECALL,
];

export const MODE_NAV: NavEntry[] = PRIMARY_NAV;

// `pinned` replaces the legacy `collapsed` state. Pinned means the
// sidebar stays expanded + content reflows around it (the original
// expanded behavior). Unpinned (the new default) means the sidebar is
// collapsed at rest + auto-expands on hover, FLOATING over content
// (no reflow) so the user's view doesn't jump every time they hover
// the rail. localStorage key migrated from bv2:sidebarCollapsed to
// preserve the prior user setting on first load: collapsed=1 → pinned=0,
// collapsed=0 → pinned=1.
const PINNED_KEY = "bv2:sidebarPinned";
const LEGACY_COLLAPSED_KEY = "bv2:sidebarCollapsed";

function readPinned(): boolean {
  try {
    if (typeof localStorage === "undefined") return false;
    const explicit = localStorage.getItem(PINNED_KEY);
    if (explicit === "1") return true;
    if (explicit === "0") return false;
    // First boot under the new scheme — migrate the old preference if
    // it exists. collapsed=1 (icons-only) → pinned=0 (auto). Otherwise
    // pinned=1 (expanded by default, like the prior behavior).
    const legacy = localStorage.getItem(LEGACY_COLLAPSED_KEY);
    if (legacy === "1") return false;
    if (legacy === "0") return true;
    // No prior preference at all: default to UNPINNED (collapsed at rest,
    // hover to expand) — the new spec.
    return false;
  } catch {
    return false;
  }
}

function persistPinned(v: boolean) {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PINNED_KEY, v ? "1" : "0");
    }
  } catch {
    /* localStorage may be unavailable; in-session state still flips. */
  }
}

// Eagerly publish the rail width on module load. When PINNED, the rail
// occupies 220px and the main content reflows. When UNPINNED, the
// content always reserves 56px regardless of hover state — the
// expanded sidebar floats over it (position: fixed already, just a
// width change) so the user's content layout doesn't jump.
if (typeof document !== "undefined") {
  document.documentElement.style.setProperty(
    "--bv2-sidebar-w",
    readPinned() ? "220px" : "56px",
  );
}

const HOVER_OUT_DELAY_MS = 300;

/** Memso left sidebar. Replaces the legacy top header + mobile bottom
 *  tab bar with a single app-shell sidebar (Linear / Notion / Slack
 *  pattern). Two desktop states (collapsed icons-only at ~56px,
 *  expanded with labels at ~220px) persist to localStorage; a mobile
 *  overlay drawer slides in from the left under 768px and closes by
 *  default. The collapsed state turns labels into right-positioned
 *  tooltips so the icon column stays scannable. */
export function Sidebar() {
  const location = useLocation();
  const auth = useAuth();
  const [pinned, setPinned] = useState<boolean>(readPinned);
  const [hovered, setHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hoverOutTimerRef = useRef<number | null>(null);

  // When pinned: sidebar always expanded + content reflows (CSS var =
  // 220px). When unpinned: content keeps 56px reservation regardless;
  // the sidebar's own width animates between 56 (idle) and 220
  // (hover) BUT floats over content (position: fixed already) so the
  // page layout doesn't jump.
  useEffect(() => {
    persistPinned(pinned);
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(
        "--bv2-sidebar-w",
        pinned ? "220px" : "56px",
      );
    }
  }, [pinned]);

  // Close the mobile drawer on every route change so tapping a nav
  // entry doesn't leave the overlay covering the freshly-routed page.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Keyboard shortcut: `[` (when no editable element is focused) or
  // Cmd/Ctrl+\ toggles PIN. Same keys as before — they used to
  // collapse/expand, now they pin/unpin (which is the new persistent
  // expanded vs auto state).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (inEditable) return;
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        setPinned((v) => !v);
      } else if (!e.metaKey && !e.ctrlKey && !e.altKey && e.key === "[") {
        e.preventDefault();
        setPinned((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Hover-in: instant. Hover-out: 300ms delay so a brief cursor exit
  // (e.g. crossing the boundary while moving to a tooltip target) doesn't
  // collapse the rail. Cancels any pending collapse on re-entry.
  const onMouseEnter = () => {
    if (pinned) return;
    if (hoverOutTimerRef.current) {
      window.clearTimeout(hoverOutTimerRef.current);
      hoverOutTimerRef.current = null;
    }
    setHovered(true);
  };
  const onMouseLeave = () => {
    if (pinned) return;
    if (hoverOutTimerRef.current) window.clearTimeout(hoverOutTimerRef.current);
    hoverOutTimerRef.current = window.setTimeout(() => {
      setHovered(false);
      hoverOutTimerRef.current = null;
    }, HOVER_OUT_DELAY_MS);
  };
  useEffect(() => () => {
    if (hoverOutTimerRef.current) window.clearTimeout(hoverOutTimerRef.current);
  }, []);

  // Close mobile drawer on Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  if (!auth.authenticated) return null;

  // Mobile drawer treats the sidebar as expanded (full labels visible)
  // since the overlay has the room. Desktop is expanded when pinned OR
  // currently hovered (auto-expand pattern). The shared body component
  // takes a single `expanded` flag so the two surfaces don't drift.
  const desktopExpanded = pinned || hovered;

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-3 focus:py-2 focus:rounded-lg focus:bg-sky-600 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>

      {/* Desktop sidebar: persistent on the left, width animates between
          collapsed (56px) and expanded (220px). Hidden on phone widths. */}
      <aside
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`hidden md:flex glass-bar fixed top-0 left-0 bottom-0 z-30 backdrop-blur border-r border-slate-200/70 dark:border-slate-700/40 flex-col transition-[width] duration-200 ease-out motion-reduce:transition-none ${
          desktopExpanded ? "w-[220px]" : "w-[56px]"
        } ${!pinned && hovered ? "shadow-2xl" : ""}`}
        aria-label="Primary"
      >
        <SidebarBody
          expanded={desktopExpanded}
          pathname={location.pathname}
          pinned={pinned}
          onTogglePin={() => setPinned((v) => !v)}
          showCollapseToggle
        />
      </aside>

      {/* Mobile hamburger: only visible when the drawer is closed. Sits
          in the top-left corner above page content. Tapping opens the
          drawer. The actual logo / nav lives inside the drawer; this
          button is purely the entry affordance. */}
      {!mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          aria-expanded={false}
          className="md:hidden fixed top-2 left-2 z-30 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/85 dark:bg-slate-800/85 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-md text-slate-700 dark:text-slate-200"
        >
          <MenuIcon className="w-5 h-5" />
        </button>
      )}

      {/* Mobile overlay drawer. The backdrop also dismisses on tap. */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fadeIn"
            aria-hidden
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-[260px] glass-bar backdrop-blur border-r border-slate-200/70 dark:border-slate-700/40 flex flex-col animate-slideInLeft"
            aria-label="Primary"
            role="dialog"
            aria-modal="true"
          >
            <SidebarBody
              expanded
              pathname={location.pathname}
              pinned
              onTogglePin={() => setMobileOpen(false)}
              showCollapseToggle
              mobileCloseIcon
            />
          </aside>
        </>
      )}
    </>
  );
}

interface SidebarBodyProps {
  expanded: boolean;
  pathname: string;
  /** True when sidebar is pinned (always-expanded + content reflows).
   *  False = auto-expand on hover, floats over content when expanded. */
  pinned: boolean;
  /** Toggle the pinned/auto state (or close the mobile drawer). */
  onTogglePin: () => void;
  showCollapseToggle: boolean;
  /** Mobile drawer renders an X (close) instead of a pin because
   *  the gesture is "close drawer", not "toggle pin". */
  mobileCloseIcon?: boolean;
}

function SidebarBody({
  expanded,
  pathname,
  pinned,
  onTogglePin,
  showCollapseToggle,
  mobileCloseIcon,
}: SidebarBodyProps) {
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Top: brand only. The pin/collapse toggle moved to the bottom
          of the sidebar (below the user menu) so its expand/collapse
          footprint change does not shift the icon column when the
          sidebar widens. Mobile drawer keeps the X-close icon up here
          because its gesture is "close drawer", not "toggle pin". */}
      <div
        className={`flex items-center px-2 pt-3 pb-2 gap-2 ${
          expanded ? "justify-between" : "flex-col"
        }`}
      >
        <Link
          to="/"
          className={`flex items-center gap-2.5 font-bold ${expanded ? "pl-1" : ""}`}
          aria-label="Memso home"
        >
          <MemsoLogo />
          {expanded && (
            <span className="tracking-tight text-base text-sky-600 dark:text-sky-400">
              Memso
            </span>
          )}
        </Link>
        {showCollapseToggle && mobileCloseIcon && (
          <button
            type="button"
            onClick={onTogglePin}
            aria-label="Close menu"
            title="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Primary nav. Active entry is highlighted with the per-tab
          accent tile; inactive entries hover-shift the row background.
          Intentionally NOT overflow-y-auto: a scroll container would
          force overflow-x:auto too, clipping the collapsed-state
          tooltips that anchor `left-full` outside the rail. The fixed
          set of nav entries fits any reasonable viewport. */}
      <nav
        className="flex-1 px-2 py-2 flex flex-col"
        aria-label="Primary navigation"
      >
        {/* Memso's own primary surfaces, in order. Today is the
            canonical landing (route "/"). */}
        <div className="space-y-1">
          {PRIMARY_NAV.map((entry) => (
            <NavRow
              key={entry.to}
              entry={entry}
              active={entry.matches(pathname)}
              expanded={expanded}
            />
          ))}
        </div>

        {/* Spacer pushes the cross-app jump-off to the bottom of the
            nav block so the divider above it reads as "this leaves
            Memso" rather than another Memso section. */}
        <div className="flex-1" aria-hidden />

        {/* Hairline divider visually separates Memso's own items from
            the cross-app jump-off. */}
        <div
          aria-hidden
          role="separator"
          className="mx-1 mb-2 border-t border-slate-200/70 dark:border-slate-700/40"
        />

        {/* Cross-app jump to WakiOutlookViewer (Mail + Calendar +
            Tasks). Brand-aware: on app.memso.ai → mail.memso.ai; on
            app.3dbypixel.com → mail.3dbypixel.com. Renders null on
            unknown hosts. The portable JWT cookie (shared with WOV's
            daemon, design doc 6.5) makes the destination land
            signed-in with zero friction. */}
        <div className="px-1">
          <CrossAppNav collapsed={!expanded} />
        </div>
      </nav>

      {/* Bottom: pinned Settings + theme controls + What's new + user. */}
      <div className="border-t border-slate-200/70 dark:border-slate-700/40 px-2 py-2 space-y-1">
        <NavRow
          entry={SETTINGS}
          active={SETTINGS.matches(pathname)}
          expanded={expanded}
        />

        <AppearanceRow expanded={expanded} />
        <WhatsNewRow expanded={expanded} />

        <UserMenuRow expanded={expanded} />
      </div>

      {/* Pin toggle at the very bottom so its footprint change between
          collapsed and expanded states does NOT shift the icons in the
          primary-nav column above it. Mobile drawer renders its X-close
          up in the top row instead (handled in the brand block). */}
      {showCollapseToggle && !mobileCloseIcon && (
        <div className="border-t border-slate-200/70 dark:border-slate-700/40 px-2 py-2">
          <button
            type="button"
            onClick={onTogglePin}
            aria-label={
              pinned
                ? "Unpin sidebar (auto-collapse on leave)"
                : "Pin sidebar (always expanded)"
            }
            title={pinned ? "Unpin ([)" : "Pin ([)"}
            className={
              "group relative flex items-center w-full rounded-lg transition-colors " +
              (pinned
                ? "text-sky-600 dark:text-sky-400 bg-sky-100/70 dark:bg-sky-900/30 hover:bg-sky-200/70 dark:hover:bg-sky-900/50"
                : "text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/40") +
              (expanded ? " gap-3 px-2 py-2" : " justify-center p-2")
            }
          >
            <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5">
              {pinned ? (
                <Pin className="w-5 h-5" />
              ) : (
                <PinOff className="w-5 h-5" />
              )}
            </span>
            {expanded && (
              <span className="text-[14px] font-medium leading-none truncate">
                {pinned ? "Unpin sidebar" : "Pin sidebar"}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/** Sidebar bottom-section trigger for the appearance popover. The
 *  dropdown anchors to this row's right edge (or top, on mobile) and
 *  closes on outside click or Esc. Single button replaces the legacy
 *  Sun/Moon toggle + Palette pair: light/dark/system live inside the
 *  popover next to the theme grid. */
function AppearanceRow({ expanded }: { expanded: boolean }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative">
      <Tooltip label="Appearance" disabled={expanded}>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Appearance"
          aria-haspopup="menu"
          aria-expanded={open}
          className={`group relative flex items-center w-full rounded-lg transition-colors ${
            open
              ? "bg-slate-200/70 dark:bg-slate-700/50 text-slate-900 dark:text-slate-100"
              : "text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
          } ${expanded ? "gap-3 px-2 py-2" : "justify-center p-2"}`}
        >
          <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5">
            <Palette className="w-5 h-5" />
          </span>
          {expanded && (
            <span className="text-[14px] font-medium leading-none truncate">
              Appearance
            </span>
          )}
        </button>
      </Tooltip>
      <ThemePickerDropdown
        open={open}
        onClose={() => setOpen(false)}
        anchor="right"
        ignoreRef={triggerRef}
      />
    </div>
  );
}

/** Self-contained "needs review" count badge (mirrors WhatsNewRow's
 *  own-hook pattern). Shows the high-confidence pending inference
 *  count (confidence > 0.5) so the user only gets nudged when Memso
 *  is fairly sure. Polls light: once on mount + every 60s. */
function NeedsReviewBadge({ expanded }: { expanded: boolean }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      fetch("/api/inference/needs-review-counts", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (alive && j) setN(Number(j.high_confidence_total) || 0);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  if (n <= 0) return null;
  return (
    <span
      className={
        "ml-auto rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center " +
        (expanded ? "" : "absolute -top-0.5 -right-0.5")
      }
      aria-label={`${n} inferences need review`}
    >
      {n}
    </span>
  );
}

function NavRow({
  entry,
  active,
  expanded,
}: {
  entry: NavEntry;
  active: boolean;
  expanded: boolean;
}) {
  const { Icon, label, to, activeTile, inactiveIconColor } = entry;
  return (
    <Tooltip label={label} disabled={expanded}>
      <Link
        to={to}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        className={`group relative flex items-center rounded-lg transition-colors ${
          expanded ? "gap-3 px-2 py-2" : "justify-center p-2"
        } ${
          active
            ? activeTile
            : "text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
        }`}
      >
        <Icon
          className={`w-5 h-5 flex-shrink-0 ${
            active ? "" : inactiveIconColor
          }`}
          strokeWidth={active ? 2.5 : 2}
        />
        {expanded && (
          <span
            className={`text-[14px] font-semibold leading-none truncate ${
              active ? "" : "text-slate-700 dark:text-slate-200"
            }`}
          >
            {label}
          </span>
        )}
        {to === "/needs-review" && <NeedsReviewBadge expanded={expanded} />}
      </Link>
    </Tooltip>
  );
}

function WhatsNewRow({ expanded }: { expanded: boolean }) {
  const { unseen } = useChangelog();
  const navigate = useNavigate();
  const has = unseen.length > 0;
  const ariaLabel = has ? `What's new (${unseen.length} new)` : "What's new";
  return (
    <Tooltip label="What's new" disabled={expanded}>
      <button
        type="button"
        onClick={() => navigate("/settings?section=whatsnew")}
        aria-label={ariaLabel}
        className={`group relative flex items-center w-full rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40 transition-colors ${
          expanded ? "gap-3 px-2 py-2" : "justify-center p-2"
        }`}
      >
        <span className="relative flex-shrink-0 inline-flex items-center justify-center w-5 h-5">
          <PartyPopper className="w-5 h-5" strokeWidth={2} />
          {has && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-white dark:ring-slate-900"
            />
          )}
        </span>
        {expanded && (
          <span className="text-[14px] font-medium leading-none truncate">What's new</span>
        )}
      </button>
    </Tooltip>
  );
}

/** Account row. Mirrors the legacy header's UserMenuStub: avatar
 *  (Google profile pic with initial fallback) opens a popover with
 *  name + email + sign-out. The trigger collapses to just the avatar
 *  when the sidebar is collapsed; the popover positions to the right
 *  of the avatar so it doesn't get clipped by the narrow rail. */
function UserMenuRow({ expanded }: { expanded: boolean }) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [installBusy, setInstallBusy] = useState(false);
  // Re-render when install-prompt state changes (capture / appinstalled).
  const [, forceInstall] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => subscribeInstall(() => forceInstall((n) => n + 1)), []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const installable = !isStandalone() && (hasNativePrompt() || isIosSafari());
  const handleInstall = async () => {
    if (hasNativePrompt()) {
      setInstallBusy(true);
      try {
        await triggerNativePrompt();
        setOpen(false);
      } finally {
        setInstallBusy(false);
      }
    } else if (isIosSafari()) {
      window.alert("On iOS Safari: tap Share, then 'Add to Home Screen.'");
      setOpen(false);
    }
  };

  if (!auth.authenticated || !auth.user) {
    return (
      <div
        className={`flex items-center rounded-lg ${
          expanded ? "gap-3 px-2 py-2" : "justify-center p-2"
        }`}
      >
        <div
          className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 ring-1 ring-slate-300 dark:ring-slate-600 flex items-center justify-center text-white font-bold text-xs select-none"
          aria-label="Account"
        >
          W
        </div>
        {expanded && (
          <span className="text-[13px] text-slate-500 dark:text-slate-400 leading-none truncate">
            {auth.loading ? "…" : "Signed out"}
          </span>
        )}
      </div>
    );
  }

  const user = auth.user;
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();
  const showImg = !!user.picture && !imgFailed;
  const display = user.name || user.email || "Account";

  return (
    <div className="relative" ref={ref}>
      <Tooltip label={display} disabled={expanded}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={`Account menu, signed in as ${display}`}
          aria-haspopup="menu"
          aria-expanded={open}
          className={`group relative flex items-center w-full rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700/40 transition-colors ${
            expanded ? "gap-3 px-2 py-2" : "justify-center p-2"
          }`}
        >
          <span className="flex-shrink-0 w-7 h-7 rounded-full overflow-hidden ring-1 ring-slate-300 dark:ring-slate-600 bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-bold text-xs select-none">
            {showImg ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setImgFailed(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              initial
            )}
          </span>
          {expanded && (
            <span className="flex-1 min-w-0 text-left text-[13px] font-medium leading-none truncate">
              {display}
            </span>
          )}
        </button>
      </Tooltip>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full mb-2 left-0 right-0 sm:left-2 sm:right-auto sm:w-64 z-50 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl shadow-slate-300/40 dark:shadow-black/50 overflow-hidden animate-fadeInUp"
        >
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {user.name || "Signed in"}
            </div>
            {user.email && (
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {user.email}
              </div>
            )}
          </div>
          {installable && (
            <button
              type="button"
              role="menuitem"
              onClick={handleInstall}
              disabled={installBusy}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/40 transition-colors disabled:opacity-60 border-b border-slate-200 dark:border-slate-700"
            >
              <Download className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              <span>{installBusy ? "Installing..." : "Install Memso as app"}</span>
            </button>
          )}
          <a
            href="/auth/logout"
            role="menuitem"
            onClick={(e) => {
              // Hot-fix (2026-05-11): without this, an open Mail page
              // with hundreds of pending /api/bridge/* fetches (from
              // the nested Daily Summary tree) could keep the browser
              // waiting up to 20 s before /auth/logout actually
              // navigates. Abort every in-flight bridge fetch + clear
              // the SPA-side cache synchronously so the click is felt
              // instantly; the browser then follows the href to the
              // server's logout flow with no in-flight requests to
              // drain.
              e.preventDefault();
              try {
                // Lazy-import to keep the Sidebar's first-render bundle
                // tight; this code only runs on the rare logout click.
                void import("../lib/bridgeFetch").then((m) => {
                  try { m.abortAllBridgeFetches(); } catch { /* noop */ }
                  try { m.clearBridgeCache(); } catch { /* noop */ }
                  window.location.assign("/auth/logout");
                });
              } catch {
                window.location.assign("/auth/logout");
              }
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </a>
        </div>
      )}
    </div>
  );
}

/** CSS-only tooltip wrapper. When the sidebar is collapsed (`disabled =
 *  false`), hovering or focusing the wrapped trigger reveals a label
 *  positioned to the right of the rail with a 200ms delay so brief
 *  cursor passes don't flash labels everywhere. When expanded, the
 *  wrapper is a passthrough. */
function Tooltip({
  label,
  disabled,
  children,
}: {
  label: string;
  disabled: boolean;
  children: ReactNode;
}) {
  if (disabled) return <>{children}</>;
  return (
    <div className="relative group/tip">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 whitespace-nowrap rounded-md bg-slate-900 dark:bg-slate-700 text-white text-xs font-medium px-2 py-1 shadow-lg opacity-0 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100 transition-opacity duration-150 delay-200 z-50"
      >
        {label}
      </span>
    </div>
  );
}

function MemsoLogo() {
  return (
    <span className="w-8 h-8 flex-shrink-0 rounded-lg overflow-hidden ring-1 ring-slate-300/60 dark:ring-white/15 shadow-sm">
      <img
        src="/icons/icon-192.png"
        alt=""
        aria-hidden
        className="w-full h-full object-cover"
      />
    </span>
  );
}

